// /src/app/modules/cuentas/components/MovimientosSection.tsx
import React, {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {fetchWithAuth} from '../../../core/utils/fetchWithAuth'
import {KTIcon} from '../../../../_metronic/helpers'
import {EditarPagoModal} from '../../movimientos/components/EditarPagoModal'
import {ConfirmarEliminarPagoModal} from '../../movimientos/components/ConfirmarEliminarPagoModal'
import type {PagoParaEditar} from '../../movimientos/components/EditarPagoModal'

type Movimiento = {
  id: string
  nroFactura: string
  idPago: string
  fecha: string
  monto: string
  tipo: string
  estado: string
  nroSocio?: string
  nombreFantasia?: string
  razonSocial?: string
  medioPago?: string
  observaciones?: string | null
  numeroRecibo?: string | null
}

type Resumen = {
  totalEgresos: number
  totalIngresos: number
  balance: number
}

interface MovimientosSectionProps {
  numeroCuenta: number
}

const MovimientosSection: React.FC<MovimientosSectionProps> = ({numeroCuenta}) => {
  const navigate = useNavigate()
  const [movimientos, setMovimientos] = useState<Movimiento[]>([])
  const [resumen, setResumen] = useState<Resumen>({totalEgresos: 0, totalIngresos: 0, balance: 0})
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalPages, setTotalPages] = useState<number>(0)
  const [total, setTotal] = useState<number>(0)
  
  // Estados para filtros
  const [fechaInicio, setFechaInicio] = useState<string>('2017-01-01')
  const [fechaFin, setFechaFin] = useState<string>(new Date().toISOString().split('T')[0])
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [mostrarFiltros, setMostrarFiltros] = useState<boolean>(false)

  // Modales editar / eliminar pago (solo para movimientos tipo HABER con idPago)
  const [pagoParaEditar, setPagoParaEditar] = useState<PagoParaEditar | null>(null)
  const [idPagoParaEliminar, setIdPagoParaEliminar] = useState<string | null>(null)

  const getToken = (): string | null => localStorage.getItem('X-TOKEN')

  const esPago = (mov: Movimiento) => mov.tipo === 'HABER' && !!mov.idPago

  const handleAuthResponse = (response: Response, data: any): boolean => {
    if (response.status === 401 || 
        (data.code === 'ERROR' && data.msg === 'No Autorizado') ||
        (data.code === 'ERROR' && data.returned_message?.includes('JWT expired'))) {
      localStorage.removeItem('X-TOKEN')
      navigate('/auth/login')
      return true
    }
    return false
  }

  const fetchMovimientos = async (page: number = 1) => {
    try {
      setLoading(true)
      setError(null)
      
      const token = getToken()
      if (!token) {
        throw new Error('No se encontró el token de autenticación')
      }

      const body: any = {
        service: 'getMovimientos',
        idCuenta: String(numeroCuenta),
        fechaInicio: fechaInicio,
        fechaFin: fechaFin,
        current_page: page,
        q: searchQuery
      }

      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-TOKEN': token
        },
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        throw new Error(`Error en la petición: ${response.status}`)
      }

      const data = await response.json()
      
      if (handleAuthResponse(response, data)) {
        return
      }
      
      if (data.code === 'OK') {
        setMovimientos(data.data.movimientos || [])
        setResumen(data.data.resumen || {totalEgresos: 0, totalIngresos: 0, balance: 0})
        setCurrentPage(data.data.page || 1)
        setTotalPages(data.data.totalPages || 0)
        setTotal(data.data.total || 0)
      } else {
        throw new Error(data.returned_message || 'Error en la respuesta del servidor')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  // Cargar movimientos automáticamente al montar el componente
  useEffect(() => {
    fetchMovimientos(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleBuscar = () => {
    setCurrentPage(1)
    fetchMovimientos(1)
  }

  const handleLimpiarFiltros = () => {
    setFechaInicio('2017-01-01')
    setFechaFin(new Date().toISOString().split('T')[0])
    setSearchQuery('')
    setCurrentPage(1)
    fetchMovimientos(1)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    fetchMovimientos(page)
  }

  return (
    <div className='card'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <i className='ki-duotone ki-chart-line-up fs-2 me-2 text-primary'>
            <span className='path1'></span>
            <span className='path2'></span>
          </i>
          <h3 className='fw-bold m-0'>Movimientos de la Cuenta</h3>
        </div>
        <div className='card-toolbar'>
          <button 
            className='btn btn-sm btn-light-primary'
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
          >
            <i className='ki-duotone ki-filter fs-3 me-1'>
              <span className='path1'></span>
              <span className='path2'></span>
            </i>
            {mostrarFiltros ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </button>
        </div>
      </div>

      <div className='card-body pt-0'>
        {/* Panel de filtros colapsable */}
        {mostrarFiltros && (
          <div className='bg-light-primary p-5 rounded mb-6'>
            <div className='row g-4 align-items-end'>
              <div className='col-md-3'>
                <label className='form-label fw-bold'>Fecha Inicio</label>
                <input
                  type='date'
                  className='form-control form-control-lg'
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                />
              </div>
              <div className='col-md-3'>
                <label className='form-label fw-bold'>Fecha Fin</label>
                <input
                  type='date'
                  className='form-control form-control-lg'
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                />
              </div>
              <div className='col-md-4'>
                <label className='form-label fw-bold'>Buscar</label>
                <input
                  type='text'
                  className='form-control form-control-lg'
                  placeholder='Buscar por factura, socio, nombre...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleBuscar()}
                />
              </div>
              <div className='col-md-2'>
                <div className='d-flex gap-2'>
                  <button 
                    className='btn btn-primary flex-grow-1'
                    onClick={handleBuscar}
                    disabled={loading}
                  >
                    <i className='ki-duotone ki-magnifier fs-3 me-1'>
                      <span className='path1'></span>
                      <span className='path2'></span>
                    </i>
                    Buscar
                  </button>
                  <button 
                    className='btn btn-light-danger'
                    onClick={handleLimpiarFiltros}
                    disabled={loading}
                  >
                    <i className='ki-duotone ki-cross fs-3'>
                      <span className='path1'></span>
                      <span className='path2'></span>
                    </i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Resumen de movimientos */}
        {movimientos.length > 0 && (
          <div className='d-flex flex-wrap gap-4 mb-6 p-4 bg-light rounded'>
            <div className='d-flex align-items-center'>
              <div className='symbol symbol-40px me-3'>
                <div className='symbol-label bg-success'>
                  <i className='ki-duotone ki-arrow-up fs-2 text-white'>
                    <span className='path1'></span>
                    <span className='path2'></span>
                  </i>
                </div>
              </div>
              <div>
                <span className='text-muted fw-semibold d-block fs-7'>Total Ingresos</span>
                <span className='text-gray-800 fw-bold fs-5'>${resumen.totalIngresos.toFixed(2)}</span>
              </div>
            </div>
            <div className='d-flex align-items-center'>
              <div className='symbol symbol-40px me-3'>
                <div className='symbol-label bg-danger'>
                  <i className='ki-duotone ki-arrow-down fs-2 text-white'>
                    <span className='path1'></span>
                    <span className='path2'></span>
                  </i>
                </div>
              </div>
              <div>
                <span className='text-muted fw-semibold d-block fs-7'>Total Egresos</span>
                <span className='text-gray-800 fw-bold fs-5'>${resumen.totalEgresos.toFixed(2)}</span>
              </div>
            </div>
            <div className='d-flex align-items-center'>
              <div className='symbol symbol-40px me-3'>
                <div className={`symbol-label ${resumen.balance >= 0 ? 'bg-primary' : 'bg-warning'}`}>
                  <i className='ki-duotone ki-chart-simple fs-2 text-white'>
                    <span className='path1'></span>
                    <span className='path2'></span>
                    <span className='path3'></span>
                    <span className='path4'></span>
                  </i>
                </div>
              </div>
              <div>
                <span className='text-muted fw-semibold d-block fs-7'>Balance</span>
                <span className='text-gray-800 fw-bold fs-5'>${resumen.balance.toFixed(2)}</span>
              </div>
            </div>
            <div className='d-flex align-items-center ms-auto'>
              <div className='symbol symbol-40px me-3'>
                <div className='symbol-label bg-info'>
                  <i className='ki-duotone ki-file-sheet fs-2 text-white'>
                    <span className='path1'></span>
                    <span className='path2'></span>
                  </i>
                </div>
              </div>
              <div>
                <span className='text-muted fw-semibold d-block fs-7'>Total Movimientos</span>
                <span className='text-gray-800 fw-bold fs-5'>{total}</span>
              </div>
            </div>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div className='table-responsive'>
            <table className='table'>
              <thead>
                <tr><th colSpan={11}>&nbsp;</th></tr>
              </thead>
              <tbody>
                {Array.from({length: 5}).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    {Array.from({length: 11}).map((__, j) => (
                      <td key={`skc-${i}-${j}`}>
                        <div className='placeholder-glow'><span className='placeholder col-8'></span></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className='alert alert-danger d-flex align-items-center p-5'>
            <i className='ki-duotone ki-information-5 fs-2hx text-danger me-4'>
              <span className='path1'></span>
              <span className='path2'></span>
              <span className='path3'></span>
            </i>
            <div className='d-flex flex-column'>
              <h4 className='mb-1 text-danger'>Error</h4>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Tabla de movimientos */}
        {!loading && !error && movimientos.length > 0 && (
          <>
            <div className='table-responsive'>
              <table className='table table-row-dashed table-hover align-middle gs-0 gy-2'>
                <thead>
                  <tr className='fw-bold text-gray-700 bg-light-secondary border-bottom-2 border-gray-300'>
                    <th className='ps-4 rounded-start' style={{minWidth: '100px'}}>Nro Factura</th>
                    <th style={{minWidth: '100px'}}>ID Pago</th>
                    <th style={{minWidth: '150px'}}>Socio</th>
                    <th style={{minWidth: '200px'}}>Nombre</th>
                    <th style={{minWidth: '120px'}}>Fecha</th>
                    <th className='text-end' style={{minWidth: '120px'}}>Monto</th>
                    <th className='text-center' style={{minWidth: '100px'}}>Tipo</th>
                    <th style={{minWidth: '120px'}}>Medio de Pago</th>
                    <th style={{minWidth: '100px'}}>Nro de Recibo</th>
                    <th className='text-center' style={{minWidth: '100px'}}>Estado</th>
                    <th className='pe-4 text-end rounded-end' style={{minWidth: '100px'}}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map((mov) => (
                    <tr key={mov.id} className='border-bottom border-gray-200'>
                      <td className='ps-4'>
                        <div className='d-flex align-items-center'>
                          <i className='ki-duotone ki-document fs-2 text-gray-400 me-2'>
                            <span className='path1'></span>
                            <span className='path2'></span>
                          </i>
                          <span className='fw-semibold text-gray-800'>#{mov.nroFactura}</span>
                        </div>
                      </td>
                      <td>
                        <span className='text-muted fw-semibold d-block fs-7'>
                          {mov.idPago || '-'}
                        </span>
                      </td>
                      <td>
                        {mov.nroSocio ? (
                          <span className='badge badge-light-info'>{mov.nroSocio}</span>
                        ) : (
                          <span className='text-muted'>-</span>
                        )}
                      </td>
                      <td>
                        <div className='d-flex flex-column'>
                          <span className='text-gray-800 fw-semibold'>{mov.nombreFantasia || '-'}</span>
                          {mov.razonSocial && mov.razonSocial !== mov.nombreFantasia && (
                            <span className='text-muted fs-7'>{mov.razonSocial}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className='text-gray-700'>{mov.fecha}</span>
                      </td>
                      <td className='text-end'>
                        <span className={`fw-bold fs-6 ${mov.tipo === 'DEBE' ? 'text-danger' : 'text-success'}`}>
                          ${parseFloat(mov.monto).toFixed(2)}
                        </span>
                      </td>
                      <td className='text-center'>
                        <span className={`badge ${mov.tipo === 'DEBE' ? 'badge-light-danger' : 'badge-light-success'} fw-semibold`}>
                          {mov.tipo === 'DEBE' ? (
                            <><i className='ki-duotone ki-arrow-down fs-4 me-1'><span className='path1'></span><span className='path2'></span></i> DEBE</>
                          ) : (
                            <><i className='ki-duotone ki-arrow-up fs-4 me-1'><span className='path1'></span><span className='path2'></span></i> HABER</>
                          )}
                        </span>
                      </td>
                      <td>
                        {mov.medioPago ? (
                          <span className='badge badge-light-primary'>{mov.medioPago}</span>
                        ) : (
                          <span className='text-muted'>-</span>
                        )}
                      </td>
                      <td>
                        <span className='text-dark fw-semibold d-block fs-7'>
                          {mov.numeroRecibo ?? '-'}
                        </span>
                      </td>
                      <td className='text-center'>
                        {mov.estado ? (
                          <span className='badge badge-light-info'>{mov.estado}</span>
                        ) : (
                          <span className='badge badge-light-secondary'>Pendiente</span>
                        )}
                      </td>
                      <td className='pe-4 text-end'>
                        {esPago(mov) ? (
                          <div className='d-flex justify-content-end flex-shrink-0 gap-1'>
                            <button
                              type='button'
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                              title='Editar pago'
                              onClick={() =>
                                setPagoParaEditar({
                                  idPago: mov.idPago,
                                  monto: mov.monto,
                                  fecha: mov.fecha,
                                  observaciones: mov.observaciones ?? null,
                                })
                              }
                            >
                              <KTIcon iconName='pencil' className='fs-3' />
                            </button>
                            <button
                              type='button'
                              className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                              title='Eliminar pago'
                              onClick={() => setIdPagoParaEliminar(mov.idPago)}
                            >
                              <KTIcon iconName='trash' className='fs-3' />
                            </button>
                          </div>
                        ) : (
                          <span className='text-muted fs-7'>-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className='d-flex flex-stack flex-wrap pt-6'>
                <div className='fs-6 fw-semibold text-gray-700'>
                  Página {currentPage} de {totalPages} - Total: {total} movimientos
                </div>
                <ul className='pagination'>
                  <li className={`page-item previous ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className='page-link'
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <i className='previous'></i>
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, idx) => {
                    const pageNum = idx + 1
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                    ) {
                      return (
                        <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                          <button
                            className='page-link'
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      )
                    } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                      return <li key={pageNum} className='page-item disabled'><span className='page-link'>...</span></li>
                    }
                    return null
                  })}
                  <li className={`page-item next ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                      className='page-link'
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <i className='next'></i>
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </>
        )}

        {/* Sin resultados */}
        {!loading && !error && movimientos.length === 0 && (
          <div className='d-flex flex-column align-items-center py-15'>
            <i className='ki-duotone ki-file-deleted fs-3x text-muted mb-4'>
              <span className='path1'></span>
              <span className='path2'></span>
            </i>
            <h5 className='text-muted mb-3'>No se encontraron movimientos</h5>
            <p className='text-muted text-center mb-5'>
              No hay movimientos registrados para esta cuenta en el rango de fechas seleccionado
            </p>
            <button 
              className='btn btn-light-primary'
              onClick={() => setMostrarFiltros(true)}
            >
              <i className='ki-duotone ki-filter fs-3 me-2'>
                <span className='path1'></span>
                <span className='path2'></span>
              </i>
              Modificar Filtros
            </button>
          </div>
        )}

        <EditarPagoModal
          isOpen={!!pagoParaEditar}
          onClose={() => setPagoParaEditar(null)}
          pago={pagoParaEditar}
          onSuccess={() => fetchMovimientos(currentPage)}
        />
        <ConfirmarEliminarPagoModal
          isOpen={!!idPagoParaEliminar}
          onClose={() => setIdPagoParaEliminar(null)}
          idPago={idPagoParaEliminar}
          onSuccess={() => fetchMovimientos(currentPage)}
        />
      </div>
    </div>
  )
}

export default MovimientosSection

